import { useMutation, useQueries, useQueryClient } from "@tanstack/react-query";
import { useContext, useEffect, useState } from "react";
import { styled } from "@mui/material";
import { Field, FormikProvider, FormikValues, useFormik } from "formik";
import * as Yup from "yup";
import Button from "../../../../customs/Button";
import Input from "../../../../customs/FormInput";
import Select from "../../../../customs/SelectInput";
import { ReactComponent as Naira } from "../../../../svg/naira.svg";
import { NotificationContext } from "../../../../providers/notification";
import apiCall from "../../../../utils/apiCall";
import apiCallFormData from "../../../../utils/apiFormDataCall";
import { reOrderFetchedData } from "../../../../hooks/FetchData";

interface ApprovalTypeData {
  ApprovalTypeId: number;
  ApprovalTypName: string;
}
export interface RouteTypeData {
  RouteId: number;
  RouteName: string;
}

interface membershipData {
  Key: number;
  Value: string;
}

interface ComponentProps {
  handleClose: () => void;
}
const CreateRoutePayment: React.FC<ComponentProps> = ({ handleClose }) => {
  const [routeName, setRouteName] = useState("");
  const [approvalName, setApprovalName] = useState("");
  const [targetMemberName, setTargetMemberName] = useState("");
  const queryClient = useQueryClient();
  const { showNotification } = useContext(NotificationContext);

  const GetApprovalTypes = async () => await apiCall().get("ApprovalType/GetApprovalTypes");
  const GetMembershipTypes = async () => await apiCall().get("Routes/GetMembershipTypes");
  const GetAllRoute = async () => await apiCall().get("Routes/GetAllRoute");

  const [getApprovalTypeQuery, getMembershipTypeQuery, getAllRouteQuery] = useQueries({
    queries: [
      { queryKey: ["get-appproval"], queryFn: GetApprovalTypes },
      { queryKey: ["get-membership"], queryFn: GetMembershipTypes },
      { queryKey: ["get-routes"], queryFn: GetAllRoute },
    ],
  });

  const approvalTypeOption = getApprovalTypeQuery?.data?.data?.map((approval: any) => (
    <option value={approval?.ApprovalTypeId} key={approval?.ApprovalTypeId}>
      {approval?.ApprovalTypName}
    </option>
  )) ?? <option disabled>{getApprovalTypeQuery?.isLoading ? "loading..." : "error"}</option>;

  const RouteTypeOption = reOrderFetchedData(getAllRouteQuery?.data?.data?.Data)?.map((route: any) => (
    <option value={route?.RouteId} key={route?.RouteId}>
      {route?.RouteName}
    </option>
  )) ?? <option disabled>{getAllRouteQuery?.isLoading ? "loading..." : "error"}</option>;

  const MembershipTypeOption = getMembershipTypeQuery?.data?.data?.map((member: any) => (
    <option value={member?.Key} key={member?.Key}>
      {member?.Value}
    </option>
  )) ?? <option disabled>{getMembershipTypeQuery?.isLoading ? "loading..." : "error"}</option>;

  // ✅ Proper mutation setup
  const postRoutePayment = (data: FormData) => {
    return apiCallFormData().post("/Routes/CreatePaymentRoute", data);
  };

  const postRoutePaymentMutation = useMutation({
    mutationFn: postRoutePayment,
    retry: 0,
  });

  const validationSchema = Yup.object().shape({
    RoutId: Yup.number().required("Route is required"),
    ActionId: Yup.number().required("Approval Type is required"),
    TargetedMembersId: Yup.number().required("Target Membership is required"),
    OrderOfPayment: Yup.number().required("Payment order is required").typeError("Must be a number"),
    Amount: Yup.number().required("Amount is required").typeError("Must be a number"),
    Amount_B: Yup.number().typeError("Must be a number"),
  });

  const formik = useFormik({
    initialValues: {
      RoutId: "", // string but parsed to int
      ActionId: "",
      TargetedMembersId: "",
      OrderOfPayment: "",
      Amount: "",
      Amount_B: "", // optional
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      const formData = new FormData();
      formData.append("RoutId", values.RoutId);
      formData.append("RouteName", routeName);
      formData.append("ActionId", values.ActionId);
      formData.append("ActionName", approvalName);
      formData.append("Amount", values.Amount);
      formData.append("Amount_B", values.Amount_B ?? "0");
      formData.append("OrderOfPayment", values.OrderOfPayment);
      formData.append("TargetedMembersId", values.TargetedMembersId);
      formData.append("TargetedMembers", targetMemberName);

      try {
        await postRoutePaymentMutation.mutateAsync(formData, {
          onSuccess: () => {
            showNotification({
              message: "Route Payment successfully added",
              type: "success",
            });
            queryClient.invalidateQueries(["get-routePayment"]);
            handleClose();
            resetForm();
          },
        });
      } catch (error: any) {
        showNotification({
          message: error?.response?.data?.Message || error?.message,
          type: "error",
        });
      }
    },
  });

  // Effects for dynamic display names
  useEffect(() => {
    const route = getAllRouteQuery?.data?.data?.Data?.find(
      (item: any) => item?.RouteId == formik?.values?.RoutId
    );
    if (route) setRouteName(route?.RouteName);
  }, [formik?.values?.RoutId]);

  useEffect(() => {
    const action = getApprovalTypeQuery?.data?.data?.find(
      (item: any) => item.ApprovalTypeId == formik?.values?.ActionId
    );
    if (action) setApprovalName(action?.ApprovalTypName);
  }, [formik?.values?.ActionId]);

  useEffect(() => {
    const member = getMembershipTypeQuery?.data?.data?.find(
      (item: any) => item.Key == formik?.values?.TargetedMembersId
    );
    if (member) setTargetMemberName(member?.Value);
  }, [formik?.values?.TargetedMembersId]);

  return (
    <FormikProvider value={formik}>
      <form onSubmit={formik.handleSubmit}>
        <StyledInputWrapper>
          <Field name="RoutId" as={Select} label="Membership Route" options={RouteTypeOption} />
          <Field name="ActionId" as={Select} label="Approval Type" options={approvalTypeOption} />
          <Field name="TargetedMembersId" as={Select} label="Target Membership" options={MembershipTypeOption} />
          <Field name="OrderOfPayment" type="number" as={Input} label="Payment Order" />
          <AmountInputWrapper>
            <StyledAmount><Naira /></StyledAmount>
            <Field name="Amount" type="text" as={StyledInput} label="Amount" />
          </AmountInputWrapper>
          <AmountInputWrapper>
            <StyledAmount><Naira /></StyledAmount>
            <Field name="Amount_B" type="text" as={StyledInput} label="NYSC Amount (optional)" />
          </AmountInputWrapper>
        </StyledInputWrapper>

        <ButtonWrapper>
          <ButtonContainer>
            <StyledButton text="Cancel" type="button" onClick={handleClose} />
            <StyledButton
              text={postRoutePaymentMutation.isLoading ? "Submitting..." : "Submit"}
              type="submit"
              disabled={postRoutePaymentMutation.isLoading}
            />
          </ButtonContainer>
        </ButtonWrapper>
      </form>
    </FormikProvider>
  );
};


export default CreateRoutePayment;

const StyledInputWrapper = styled("div")`
  display: grid;
  flex-flow: row wrap;
  align-items: start;
  column-gap: 4rem;
  margin-inline: 4rem;

  @media (min-width: 1038px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 587px) {
    justify-content: center;
    grid-template-columns: repeat(1, 1fr);
  }
`;

const ButtonWrapper = styled("div")`
  display: flex;
  justify-content: flex-end;
  margin-block: 2.5rem;
  padding-inline: 3.5rem;
`;

const ButtonContainer = styled("div")`
  display: flex;
  gap: 2rem;
`;

const StyledButton = styled(Button)`
  padding-block: 1rem;
  padding-inline: 2rem;
`;

const StyledAmount = styled("span")`
  background-color: #11643c;
  border: 0.1rem solid #11643c;
  padding: 1rem 1.2rem;
  position: absolute;
  top: 48.1px;
  z-index: 9;
`;

const AmountInputWrapper = styled("div")`
  position: relative;
`;

const StyledInput = styled(Input)`
  padding: 1.2rem 4.4rem;
`;
